import {
  OFFER_STATUS,
  OFFER_STATUS_TRANSITIONS,
  PRICING_TYPE,
} from "../constants/offer.constants";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const isEmpty = (value) => {
  return (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  );
};


const isNonNegativeNumber = (value) => {
  if (isEmpty(value)) {
    return true;
  }

  const number = Number(value);

  return (
    Number.isFinite(number) &&
    number >= 0
  );
};


const isPositiveNumber = (value) => {
  const number = Number(value);

  return (
    Number.isFinite(number) &&
    number > 0
  );
};


/*
|--------------------------------------------------------------------------
| OFFER ITEM VALIDATION
|--------------------------------------------------------------------------
*/

export const validateOfferItem = (
  item,
  index
) => {
  const errors = {};

  const prefix = `items.${index}`;


  /*
  |----------------------------------------------------------------------
  | DESCRIPTION
  |----------------------------------------------------------------------
  */

  if (
    isEmpty(item.description)
  ) {
    errors[`${prefix}.description`] =
      "Item description is required";
  }


  /*
  |----------------------------------------------------------------------
  | QUANTITY
  |----------------------------------------------------------------------
  */

  if (
    !isPositiveNumber(
      item.quantity ?? 1
    )
  ) {
    errors[`${prefix}.quantity`] =
      "Quantity must be greater than zero";
  }


  /*
  |----------------------------------------------------------------------
  | UNIT PRICE
  |----------------------------------------------------------------------
  */

  if (
    item.unitPrice !== undefined &&
    item.unitPrice !== null &&
    item.unitPrice !== "" &&
    !isNonNegativeNumber(item.unitPrice)
  ) {
    errors[`${prefix}.unitPrice`] =
      "Unit price must be a non-negative number";
  }


  /*
  |----------------------------------------------------------------------
  | DISCOUNT
  |----------------------------------------------------------------------
  */

  if (
    !isNonNegativeNumber(
      item.discountAmount
    )
  ) {
    errors[`${prefix}.discountAmount`] =
      "Item discount must be a non-negative number";
  }


  /*
  |----------------------------------------------------------------------
  | TAX
  |----------------------------------------------------------------------
  */

  if (
    !isNonNegativeNumber(
      item.taxAmount
    )
  ) {
    errors[`${prefix}.taxAmount`] =
      "Item tax must be a non-negative number";
  }


  return errors;
};


/*
|--------------------------------------------------------------------------
| OFFER ITEMS VALIDATION
|--------------------------------------------------------------------------
*/

export const validateOfferItems = (
  items
) => {
  const errors = {};

  if (
    !Array.isArray(items) ||
    items.length === 0
  ) {
    errors.items =
      "At least one offer item is required";

    return errors;
  }


  items.forEach(
    (item, index) => {
      Object.assign(
        errors,
        validateOfferItem(
          item,
          index
        )
      );
    }
  );


  return errors;
};


/*
|--------------------------------------------------------------------------
| CREATE OFFER VALIDATION
|--------------------------------------------------------------------------
*/

export const validateCreateOffer = (
  data
) => {
  const errors = {};


  /*
  |----------------------------------------------------------------------
  | QUERY
  |----------------------------------------------------------------------
  */

  if (
    isEmpty(data.queryId)
  ) {
    errors.queryId =
      "Query is required";
  }


  /*
  |----------------------------------------------------------------------
  | ITEMS
  |----------------------------------------------------------------------
  */

  Object.assign(
    errors,
    validateOfferItems(
      data.items
    )
  );


  /*
  |----------------------------------------------------------------------
  | DISCOUNT
  |----------------------------------------------------------------------
  */

  if (
    !isNonNegativeNumber(
      data.discountAmount
    )
  ) {
    errors.discountAmount =
      "Discount amount must be a non-negative number";
  }


  /*
  |----------------------------------------------------------------------
  | TAX
  |----------------------------------------------------------------------
  */

  if (
    !isNonNegativeNumber(
      data.taxAmount
    )
  ) {
    errors.taxAmount =
      "Tax amount must be a non-negative number";
  }


  /*
  |----------------------------------------------------------------------
  | CURRENCY
  |----------------------------------------------------------------------
  */

  if (
    data.currency !== undefined &&
    data.currency !== null &&
    (
      typeof data.currency !== "string" ||
      data.currency.trim().length !== 3
    )
  ) {
    errors.currency =
      "Currency must contain exactly 3 characters";
  }


  /*
  |----------------------------------------------------------------------
  | VALID UNTIL
  |----------------------------------------------------------------------
  */

  if (
    data.validUntil !== undefined &&
    data.validUntil !== null &&
    data.validUntil !== "" &&
    typeof data.validUntil !== "string"
  ) {
    errors.validUntil =
      "Valid until must be a valid date";
  }


  /*
  |----------------------------------------------------------------------
  | NOTES
  |----------------------------------------------------------------------
  */

  if (
    data.notes !== undefined &&
    data.notes !== null &&
    typeof data.notes !== "string"
  ) {
    errors.notes =
      "Notes must be a string";
  }


  return errors;
};


/*
|--------------------------------------------------------------------------
| UPDATE OFFER VALIDATION
|--------------------------------------------------------------------------
*/

export const validateUpdateOffer = (
  data
) => {
  const errors = {};


  if (
    !isNonNegativeNumber(
      data.discountAmount
    )
  ) {
    errors.discountAmount =
      "Discount amount must be a non-negative number";
  }


  if (
    !isNonNegativeNumber(
      data.taxAmount
    )
  ) {
    errors.taxAmount =
      "Tax amount must be a non-negative number";
  }


  if (
    data.currency !== undefined &&
    data.currency !== null &&
    (
      typeof data.currency !== "string" ||
      data.currency.trim().length !== 3
    )
  ) {
    errors.currency =
      "Currency must contain exactly 3 characters";
  }


  if (
    data.validUntil !== undefined &&
    data.validUntil !== null &&
    data.validUntil !== "" &&
    typeof data.validUntil !== "string"
  ) {
    errors.validUntil =
      "Valid until must be a valid date";
  }


  if (
    data.notes !== undefined &&
    data.notes !== null &&
    typeof data.notes !== "string"
  ) {
    errors.notes =
      "Notes must be a string";
  }


  return errors;
};


/*
|--------------------------------------------------------------------------
| STATUS VALIDATION
|--------------------------------------------------------------------------
*/

export const validateOfferStatus = (
  status
) => {
  const errors = {};


  if (!status) {
    errors.status =
      "Status is required";

    return errors;
  }


  if (
    !Object.values(
      OFFER_STATUS
    ).includes(status)
  ) {
    errors.status =
      "Invalid offer status";
  }


  return errors;
};


/*
|--------------------------------------------------------------------------
| STATUS TRANSITION VALIDATION
|--------------------------------------------------------------------------
*/

export const canChangeOfferStatus = (
  currentStatus,
  nextStatus
) => {
  return (
    OFFER_STATUS_TRANSITIONS[
      currentStatus
    ]?.includes(nextStatus) || false
  );
};


/*
|--------------------------------------------------------------------------
| PRICING VALIDATION
|--------------------------------------------------------------------------
*/

export const validatePricingSelection = ({
  pricingPlan,
  unitPrice
}) => {
  const errors = {};


  if (!pricingPlan) {
    if (
      isEmpty(unitPrice)
    ) {
      errors.unitPrice =
        "Unit price is required";
    }

    return errors;
  }


  if (
    pricingPlan.pricing_type ===
    PRICING_TYPE.FIXED
  ) {
    return errors;
  }


  if (
    pricingPlan.pricing_type ===
    PRICING_TYPE.CUSTOM &&
    isEmpty(unitPrice)
  ) {
    errors.unitPrice =
      "Unit price is required for custom pricing";
  }


  return errors;
};